export const OKRS_PAGE_SIZE = 10;
export const FILTER_ALL = "all" as const;

export type SortDirection = "asc" | "desc";

export type SortState = {
  direction: SortDirection;
  key: string;
} | null;

export type OkrsFilterSpec<T> = {
  allLabel: string;
  getValue: (row: T) => string;
  id: string;
  label: string;
  options?: Array<{ label: string; value: string }>;
};

export function filterBySearch<T>(rows: T[], search: string, searchText: (row: T) => string): T[] {
  const query = search.trim().toLowerCase();
  if (!query) return rows;
  return rows.filter((row) => searchText(row).toLowerCase().includes(query));
}

export function applyColumnFilters<T>(
  rows: T[],
  filters: Array<{ getValue: (row: T) => string; value: string }>,
): T[] {
  return rows.filter((row) =>
    filters.every((filter) => filter.value === FILTER_ALL || filter.getValue(row) === filter.value),
  );
}

export function sortRows<T>(
  rows: T[],
  sort: SortState,
  getSortValue: (row: T, key: string) => string | number,
): T[] {
  if (!sort) return rows;
  const indexed = rows.map((row, index) => ({ index, row }));
  indexed.sort((left, right) => {
    const compared = compareSortValues(
      getSortValue(left.row, sort.key),
      getSortValue(right.row, sort.key),
    );
    if (compared !== 0) return sort.direction === "asc" ? compared : -compared;
    return left.index - right.index;
  });
  return indexed.map((entry) => entry.row);
}

export function paginateRows<T>(
  rows: T[],
  page: number,
  pageSize: number = OKRS_PAGE_SIZE,
): {
  end: number;
  page: number;
  pageCount: number;
  pageRows: T[];
  start: number;
  total: number;
} {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const startIndex = (safePage - 1) * pageSize;
  const pageRows = rows.slice(startIndex, startIndex + pageSize);
  return {
    end: Math.min(startIndex + pageRows.length, rows.length),
    page: safePage,
    pageCount,
    pageRows,
    start: rows.length === 0 ? 0 : startIndex + 1,
    total: rows.length,
  };
}

export function queryOkrsTable<T>(args: {
  filters: Array<{ getValue: (row: T) => string; value: string }>;
  getSortValue: (row: T, key: string) => string | number;
  page: number;
  pageSize?: number;
  rows: T[];
  search: string;
  searchText: (row: T) => string;
  sort: SortState;
}): ReturnType<typeof paginateRows<T>> & { filtered: T[] } {
  const searched = filterBySearch(args.rows, args.search, args.searchText);
  const filtered = applyColumnFilters(searched, args.filters);
  const sorted = sortRows(filtered, args.sort, args.getSortValue);
  return { ...paginateRows(sorted, args.page, args.pageSize ?? OKRS_PAGE_SIZE), filtered };
}

export function uniqueFilterOptions<T>(
  rows: T[],
  getValue: (row: T) => string,
  labels?: Record<string, string>,
): Array<{ label: string; value: string }> {
  const values = [...new Set(rows.map(getValue).filter((value) => value.trim().length > 0))].sort(
    (left, right) => left.localeCompare(right),
  );
  return values.map((value) => ({ label: labels?.[value] ?? value, value }));
}

export function csvEscape(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

export function stringifyCsv(
  header: readonly string[],
  rows: readonly (readonly string[])[],
): string {
  return [header, ...rows].map((line) => line.map((cell) => csvEscape(cell)).join(",")).join("\n");
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function compareSortValues(left: string | number, right: string | number): number {
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}
